import { useState, useEffect, useCallback } from 'react';
import { get, post, put, del } from '../api';

// ── Local UI primitives (same look as the rest of the hub) ───────────────────
function Btn({ onClick, children, variant = 'primary', small, disabled, style = {} }) {
  const s = { primary: { background: '#c9a96e', color: '#1a1208', border: 'none' }, secondary: { background: 'transparent', color: '#8a7055', border: '1px solid #3a2a12' }, danger: { background: '#e63c3c22', color: '#e63c3c', border: '1px solid #e63c3c44' }, success: { background: '#10b98122', color: '#10b981', border: '1px solid #10b98144' }, ghost: { background: '#c9a96e18', color: '#c9a96e', border: '1px solid #c9a96e44' } };
  return <button onClick={onClick} disabled={disabled} style={{ ...s[variant], borderRadius: 8, padding: small ? '6px 14px' : '10px 22px', fontWeight: 700, cursor: disabled ? 'default' : 'pointer', fontSize: small ? 12 : 14, opacity: disabled ? 0.5 : 1, fontFamily: 'inherit', ...style }}>{children}</button>;
}
function Input({ label, value, onChange, placeholder, type = 'text', rows }) {
  const s = { width: '100%', background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 8, padding: '10px 14px', color: '#f5e6c8', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };
  return (<div style={{ marginBottom: 14 }}>{label && <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>}{rows ? <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows} style={{ ...s, resize: 'vertical' }} /> : <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={s} />}</div>);
}
function Modal({ children, onClose, wide }) {
  return (<div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}><div style={{ background: '#1a1208', border: '1px solid #3a2a12', borderRadius: 14, padding: 32, maxWidth: wide ? 900 : 660, width: '100%', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>{children}</div></div>);
}
function Badge({ label, color }) {
  return <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</span>;
}
function Select({ label, value, onChange, options }) {
  const s = { width: '100%', background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 8, padding: '10px 14px', color: '#f5e6c8', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' };
  return (<div style={{ marginBottom: 14 }}>{label && <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>}<select value={value} onChange={onChange} style={s}>{options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>);
}

// ── Bullet list editor (responsibilities / requirements) ─────────────────────
function BulletEditor({ label, items, onChange }) {
  const [draft, setDraft] = useState('');
  function add() { const v = draft.trim(); if (!v) return; onChange([...(items || []), v]); setDraft(''); }
  function remove(i) { onChange(items.filter((_, idx) => idx !== i)); }
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>
      {(items || []).map((it, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ color: '#c9a96e', fontSize: 14 }}>•</span>
          <span style={{ flex: 1, color: '#f5e6c8', fontSize: 13 }}>{it}</span>
          <button onClick={() => remove(i)} style={{ background: 'none', border: 'none', color: '#e63c3c', cursor: 'pointer', fontSize: 16, fontFamily: 'inherit' }}>×</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} placeholder="Add a bullet and press Enter" style={{ flex: 1, background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 8, padding: '8px 12px', color: '#f5e6c8', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
        <Btn small variant="ghost" onClick={add}>Add</Btn>
      </div>
    </div>
  );
}

// ── Create / edit posting modal ──────────────────────────────────────────────
function PostingModal({ posting, onClose, onSaved }) {
  const blank = { title: '', department: 'Front of House', emp_type: 'Part-time', pay: '', status: 'draft', responsibilities: [], requirements: [] };
  const [form, setForm] = useState(posting ? { ...blank, ...posting } : blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function save() {
    if (!form.title.trim()) { setError('Title is required'); return; }
    setSaving(true); setError('');
    try {
      const body = { title: form.title, department: form.department, emp_type: form.emp_type, pay: form.pay, status: form.status, responsibilities: form.responsibilities, requirements: form.requirements };
      if (posting) await put(`/careers/postings/${posting.id}`, body);
      else await post('/careers/postings', body);
      onSaved();
      onClose();
    } catch (e) { setError(e.message); setSaving(false); }
  }

  return (
    <Modal onClose={onClose} wide>
      <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: '#f5e6c8', marginBottom: 20 }}>{posting ? 'Edit Posting' : 'New Posting'}</h2>
      <Input label="Title" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Guest Experience Team Member" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Select label="Department" value={form.department} onChange={e => set('department', e.target.value)} options={[{ value: 'Front of House', label: 'Front of House' }, { value: 'Back of House', label: 'Back of House' }, { value: 'Management', label: 'Management' }]} />
        <Select label="Employment Type" value={form.emp_type} onChange={e => set('emp_type', e.target.value)} options={[{ value: 'Part-time', label: 'Part-time' }, { value: 'Full-time', label: 'Full-time' }, { value: 'Part-time • Full-time', label: 'Part-time • Full-time' }, { value: 'Seasonal', label: 'Seasonal' }]} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Input label="Pay" value={form.pay} onChange={e => set('pay', e.target.value)} placeholder="e.g. $20–$22/hr + Pooled Tips" />
        <Select label="Status" value={form.status} onChange={e => set('status', e.target.value)} options={[{ value: 'draft', label: 'Draft (hidden)' }, { value: 'live', label: 'Live (on careers page)' }]} />
      </div>
      <BulletEditor label="Responsibilities" items={form.responsibilities} onChange={v => set('responsibilities', v)} />
      <BulletEditor label="Requirements" items={form.requirements} onChange={v => set('requirements', v)} />
      {error && <p style={{ color: '#e63c3c', fontSize: 13, marginBottom: 12 }}>{error}</p>}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Posting'}</Btn>
      </div>
    </Modal>
  );
}

// ── Careers admin view (exported) ────────────────────────────────────────────
export function CareersAdminView() {
  const [postings, setPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // posting object or 'new'
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const rows = await get('/careers/postings'); setPostings(rows); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function toggleStatus(p) {
    setBusy(true);
    try { await put(`/careers/postings/${p.id}`, { status: p.status === 'live' ? 'draft' : 'live' }); await load(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  }
  async function remove(p) {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    setBusy(true);
    try { await del(`/careers/postings/${p.id}`); await load(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  }
  async function move(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= postings.length) return;
    const next = [...postings];
    const tmp = next[i]; next[i] = next[j]; next[j] = tmp;
    setPostings(next);
    setBusy(true);
    try { await put('/careers/postings-reorder', { order: next.map(p => p.id) }); }
    catch (e) { alert(e.message); await load(); }
    finally { setBusy(false); }
  }

  const liveCount = postings.filter(p => p.status === 'live').length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 16 }}>
        <div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#f5e6c8', marginBottom: 8 }}>Careers Postings</h2>
          <p style={{ color: '#8a7055', marginBottom: 0 }}>Manage the roles shown on the public careers page. <strong style={{ color: '#c9a96e' }}>{liveCount} live</strong> · {postings.length - liveCount} draft.</p>
        </div>
        <Btn onClick={() => setEditing('new')}>+ New Posting</Btn>
      </div>
      <div style={{ background: '#c9a96e12', border: '1px solid #c9a96e33', borderRadius: 10, padding: '12px 16px', margin: '16px 0 24px', color: '#c9a96e', fontSize: 13 }}>
        Only <strong>Live</strong> postings appear on teeandtimber.com/careers. Drafts are saved here but hidden from the public.
      </div>

      {loading ? <p style={{ color: '#5a4030' }}>Loading postings…</p> : (
        postings.length === 0 ? <p style={{ color: '#5a4030' }}>No postings yet. Click “New Posting” to add one.</p> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {postings.map((p, i) => {
              const live = p.status === 'live';
              return (
                <div key={p.id} style={{ background: '#1a1208', border: `1px solid ${live ? '#10b98144' : '#3a2a12'}`, borderRadius: 12, padding: 18, display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button onClick={() => move(i, -1)} disabled={i === 0 || busy} style={{ background: 'none', border: 'none', color: i === 0 ? '#3a2a12' : '#8a7055', cursor: i === 0 ? 'default' : 'pointer', fontSize: 14, fontFamily: 'inherit' }}>▲</button>
                    <button onClick={() => move(i, 1)} disabled={i === postings.length - 1 || busy} style={{ background: 'none', border: 'none', color: i === postings.length - 1 ? '#3a2a12' : '#8a7055', cursor: i === postings.length - 1 ? 'default' : 'pointer', fontSize: 14, fontFamily: 'inherit' }}>▼</button>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', fontSize: 17 }}>{p.title}</span>
                      <Badge label={live ? 'Live' : 'Draft'} color={live ? '#10b981' : '#8a7055'} />
                    </div>
                    <div style={{ color: '#8a7055', fontSize: 13 }}>{p.department} · {p.emp_type}{p.pay ? ` · ${p.pay}` : ''}</div>
                    <div style={{ color: '#5a4030', fontSize: 12, marginTop: 2 }}>{(p.responsibilities || []).length} responsibilities · {(p.requirements || []).length} requirements</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <Btn small variant={live ? 'secondary' : 'success'} onClick={() => toggleStatus(p)} disabled={busy}>{live ? 'Unpublish' : 'Publish'}</Btn>
                    <Btn small variant="ghost" onClick={() => setEditing(p)}>Edit</Btn>
                    <Btn small variant="danger" onClick={() => remove(p)} disabled={busy}>Delete</Btn>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {editing && <PostingModal posting={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
